import React from 'react';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

type ConditionalFieldPresence = {
  hasFixedTerm: boolean;
  hasOtherEmployment: boolean;
  includeAddressFields: boolean;
};

type ConditionalsPanelProps = {
  values: {
    is_fixed_term: boolean | string;
    contract_end_date?: string;
    fixed_term_reason?: string;
    has_other_employment: boolean | string;
    other_employment_employer?: string;
    other_employment_street?: string;
    other_employment_house_no?: string;
    other_employment_postal_code?: string;
    other_employment_city?: string;
    other_employment_start_date?: string;
    other_employment_monthly_salary?: string;
  };
  onChange: (patch: Partial<ConditionalsPanelProps['values']>) => void;
  presence: ConditionalFieldPresence;
  errors?: Record<string, string>;
};

const inputStyle: React.CSSProperties = ps.inputStyle;

const labelStyle: React.CSSProperties = ps.labelStyle;

const errorStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: colors.danger,
};

export function ConditionalsPanel({ values, onChange, presence, errors = {} }: ConditionalsPanelProps) {
  const isFixedTermChecked = values.is_fixed_term === true || values.is_fixed_term === 'true';
  const hasOtherEmploymentChecked = values.has_other_employment === true || values.has_other_employment === 'true';

  return (
    <div style={{ maxWidth: 800 }}>
      {presence.hasFixedTerm && (
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            border: `2px solid ${colors.border}`,
            borderRadius: 8,
            backgroundColor: colors.bgMuted,
          }}
        >
          <label style={{ ...labelStyle, fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 12 }}>
            Befristung (§ 1)
          </label>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isFixedTermChecked}
                onChange={(e) => onChange({ is_fixed_term: e.target.checked })}
                style={{ cursor: 'pointer', width: 18, height: 18 }}
              />
              <span style={{ fontSize: 14, fontWeight: 500, color: colors.textBody }}>
                Befristet?
              </span>
            </label>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4, marginLeft: 26 }}>
              Wenn aktiviert, wird das Enddatum im Vertrag angegeben (§ 1 Befristung).
            </div>
          </div>

          {isFixedTermChecked && (
            <div
              style={{
                padding: 16,
                backgroundColor: colors.bgCard,
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Befristet bis *</label>
                <input
                  type="date"
                  value={values.contract_end_date || ''}
                  onChange={(e) => onChange({ contract_end_date: e.target.value })}
                  style={{
                    ...inputStyle,
                    borderColor: errors.contract_end_date ? colors.danger : colors.border,
                  }}
                />
                {errors.contract_end_date && (
                  <div style={errorStyle}>{errors.contract_end_date}</div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Befristungsgrund (optional)</label>
                <input
                  type="text"
                  value={values.fixed_term_reason || ''}
                  onChange={(e) => onChange({ fixed_term_reason: e.target.value })}
                  placeholder="z.B. Projektbezogen, Vertretung, Saison"
                  style={inputStyle}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {presence.hasOtherEmployment && (
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            border: `2px solid ${colors.border}`,
            borderRadius: 8,
            backgroundColor: colors.bgMuted,
          }}
        >
          <label style={{ ...labelStyle, fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 12 }}>
            Weitere Beschäftigung (§ 7)
          </label>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasOtherEmploymentChecked}
                onChange={(e) => onChange({ has_other_employment: e.target.checked })}
                style={{ cursor: 'pointer', width: 18, height: 18 }}
              />
              <span style={{ fontSize: 14, fontWeight: 500, color: colors.textBody }}>
                Hat weitere Beschäftigung?
              </span>
            </label>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4, marginLeft: 26 }}>
              Wenn aktiviert, wird die lange Klausel mit Details zum weiteren Arbeitgeber verwendet.
            </div>
          </div>

          {hasOtherEmploymentChecked && (
            <div
              style={{
                padding: 16,
                backgroundColor: colors.bgCard,
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Weiterer Arbeitgeber (Name) *</label>
                <input
                  type="text"
                  value={values.other_employment_employer || ''}
                  onChange={(e) => onChange({ other_employment_employer: e.target.value })}
                  placeholder="z.B. Muster GmbH"
                  style={{
                    ...inputStyle,
                    borderColor: errors.other_employment_employer ? colors.danger : colors.border,
                  }}
                />
                {errors.other_employment_employer && (
                  <div style={errorStyle}>{errors.other_employment_employer}</div>
                )}
              </div>

              {presence.includeAddressFields && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>Straße *</label>
                    <input
                      type="text"
                      value={values.other_employment_street || ''}
                      onChange={(e) => onChange({ other_employment_street: e.target.value })}
                      placeholder="z.B. Berliner Str."
                      style={{
                        ...inputStyle,
                        borderColor: errors.other_employment_street ? colors.danger : colors.border,
                      }}
                    />
                    {errors.other_employment_street && (
                      <div style={errorStyle}>{errors.other_employment_street}</div>
                    )}
                  </div>

                  <div style={{ marginBottom: 12, display: 'flex', gap: 12 }}>
                    <div style={{ flex: '0 0 30%' }}>
                      <label style={labelStyle}>Hausnr. *</label>
                      <input
                        type="text"
                        value={values.other_employment_house_no || ''}
                        onChange={(e) => onChange({ other_employment_house_no: e.target.value })}
                        placeholder="11"
                        style={{
                          ...inputStyle,
                          borderColor: errors.other_employment_house_no ? colors.danger : colors.border,
                        }}
                      />
                      {errors.other_employment_house_no && (
                        <div style={errorStyle}>{errors.other_employment_house_no}</div>
                      )}
                    </div>
                    <div style={{ flex: '0 0 30%' }}>
                      <label style={labelStyle}>PLZ *</label>
                      <input
                        type="text"
                        value={values.other_employment_postal_code || ''}
                        onChange={(e) => onChange({ other_employment_postal_code: e.target.value })}
                        placeholder="10115"
                        style={{
                          ...inputStyle,
                          borderColor: errors.other_employment_postal_code ? colors.danger : colors.border,
                        }}
                      />
                      {errors.other_employment_postal_code && (
                        <div style={errorStyle}>{errors.other_employment_postal_code}</div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Stadt *</label>
                      <input
                        type="text"
                        value={values.other_employment_city || ''}
                        onChange={(e) => onChange({ other_employment_city: e.target.value })}
                        placeholder="Berlin"
                        style={{
                          ...inputStyle,
                          borderColor: errors.other_employment_city ? colors.danger : colors.border,
                        }}
                      />
                      {errors.other_employment_city && (
                        <div style={errorStyle}>{errors.other_employment_city}</div>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Beginn der weiteren Beschäftigung *</label>
                <input
                  type="date"
                  value={values.other_employment_start_date || ''}
                  onChange={(e) => onChange({ other_employment_start_date: e.target.value })}
                  style={{
                    ...inputStyle,
                    borderColor: errors.other_employment_start_date ? colors.danger : colors.border,
                  }}
                />
                {errors.other_employment_start_date && (
                  <div style={errorStyle}>{errors.other_employment_start_date}</div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Monatliches Entgelt (EUR) *</label>
                <input
                  type="text"
                  value={values.other_employment_monthly_salary || ''}
                  onChange={(e) => onChange({ other_employment_monthly_salary: e.target.value })}
                  placeholder="z.B. 450.00"
                  style={{
                    ...inputStyle,
                    borderColor: errors.other_employment_monthly_salary ? colors.danger : colors.border,
                  }}
                />
                {errors.other_employment_monthly_salary && (
                  <div style={errorStyle}>{errors.other_employment_monthly_salary}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
